import { PriceFunc, PriceElemsData, PriceSegment } from '@icc/shared/types/price/Prices';
import { Injectable } from '@angular/core';
import { Common } from '@icc/shared/helpers';
import { TaxRatesService } from '@icc/b2b/shared';

@Injectable()
export class PriceExternalVenetianService {
    constructor() {}

    @PriceFunc({
        shortName: 'externalVenetianRaster',
        data: {
            shutterType: 'conf.shutterType',
            lamella: 'conf.lamella',
            coverType: 'conf.coverType',
            cover: 'conf.cover',
            boxPaintedSide: 'conf.boxPaintedSide',
            guides: 'conf.guides',
            drives: 'conf.drives',
            noneDrive: 'conf.noneDrive',
            shutters: 'conf.shutters',
            blindGuidanceType: 'conf.blindGuidanceType',
            layout: 'conf.layout',
            rasters: 'priceRasters',
            prices: 'prices',
        },
    })
    contructionPrice(
        { NoPriceCauses }: PriceElemsData,
        {
            shutterType,
            lamella,
            coverType,
            cover,
            boxPaintedSide,
            guides,
            shutters,
            rasters,
            drives,
            noneDrive,
            blindGuidanceType,
            layout,
            prices,
        }
    ): PriceSegment[] {
        const priceSegments: PriceSegment[] = [];
        if (!shutterType?.id || shutterType?.ignorePriceRaster) {
            return priceSegments;
        }

        const priceRasters = rasters?.external_venetian?.filter((el) => {
            let match = true;
            if (el.relations.ShutterTypes?.length && shutterType?.id) {
                match = match && el.relations.ShutterTypes.map(Number).includes(+shutterType.id);
            }
            if (el.relations.Lamellas?.length && lamella?.id) {
                match = match && el.relations.Lamellas.map(Number).includes(+lamella.id);
            }
            if (el.relations.BlindBoxesTypes?.length && coverType?.id) {
                match = match && el.relations.BlindBoxesTypes.map(Number).includes(+coverType.id);
            }
            if (el.relations.BoxColorTypes?.length) {
                match = match && el.relations.BoxColorTypes.includes(boxPaintedSide);
            }
            return match;
        });
        if (priceRasters?.length > 0) {
            shutters.forEach((shutter) => {
                const shutterGuides = guides?.find((el) => el.shutterId === shutter.id);
                const shutterDrive = drives?.find((el) => el.shutterId === shutter.id);
                const leftConnection = layout?.connections?.find((el) => el.next === shutter.id);
                const rightConnection = layout?.connections?.find((el) => el.previous === shutter.id);
                const priceRaster = priceRasters
                    .sort((a,b) => Number(b.relations.LeadingTypes != null) - Number(a.relations.LeadingTypes != null))
                    .find((el) => {
                        let match = true;
                        if (el.relations.LeadingTypes) {
                            match =
                                match &&
                                el.relations.LeadingTypes.includes(
                                    shutterGuides?.left?.type === 'blind_guidance' ||
                                        shutterGuides?.right?.type === 'blind_guidance'
                                        ? 'blind_guidance'
                                        : 'cord'
                                );
                        }
                        if (el.relations.Drives) {
                            if (shutterDrive?.hasDrive) {
                                match =
                                    match &&
                                    el.relations.Drives.map(String).includes(shutterDrive?.driveId?.toString());
                            } else if (noneDrive?.driveId) {
                                match =
                                    match &&
                                    el.relations.Drives.map(String).includes(noneDrive?.driveId?.toString());
                            }
                        }
                        return match;
                    });
                const height = shutterType?.onlyCover ? (cover?.height ?? cover?.size) :  Math.max(shutter.height, shutter.externalHeight || 0);
                const width = shutter.billingWidth ?? shutter.width;
                const price = priceRaster?.raster.find(
                    (el) =>
                        +el.d[0][0] <= +width &&
                        +el.d[0][1] >= +width &&
                        +el.d[1][0] <= +height &&
                        +el.d[1][1] >= +height
                );
                if (price) {
                    priceSegments.push({
                        type: 'externalVenetianRaster',
                        baseValue: price.p,
                        value: price.p,
                        valueIsGross: priceRaster.is_gross,
                        taxRate: priceRaster.tax_rate,
                        valueCurrency: priceRaster.currency,
                        valueType: 'value',
                        data: {
                            shutterId: shutter.id,
                            width,
                            height,
                            shutterWidth: shutter.width,
                            bundleWidth: shutter.bundleWidth,
                            shutterHeight: Math.max(shutter.height, shutter.externalHeight || 0),
                            rasterId: priceRaster.id,
                            rasterExtId: priceRaster.ext_id,
                        },
                    });
                } else {
                    priceSegments.push({
                        type: 'externalVenetianRaster',
                        baseValue: null,
                        value: null,
                        valueType: 'value',
                        valueIsGross: priceRaster?.is_gross,
                        taxRate: priceRaster?.tax_rate,
                        valueCurrency: priceRaster?.currency,
                        data: {
                            shutterId: shutter.id,
                            width,
                            height,
                            shutterWidth: shutter.width,
                            bundleWidth: shutter.bundleWidth,
                            rasterId: priceRaster?.id,
                            rasterExtId: priceRaster?.ext_id,
                        },
                    });
                }
            });
        } else {
            priceSegments.push({
                type: 'externalVenetianRaster',
                baseValue: null,
                value: null,
                valueType: 'value',
                data: {},
            });
        }
        if (!shutters.length) {
            priceSegments.push({
                type: 'externalVenetianRaster',
                baseValue: null,
                value: null,
                valueType: 'value',
                data: {},
            });
        }

        return priceSegments;
    }

    @PriceFunc({
        shortName: 'externalVenetianLamellaRotation',
        data: {
            shutterType: 'conf.shutterType',
            lamella: 'conf.lamella',
            prices: 'prices',
        },
    })
    lamellaRotation(
        { NoPriceCauses }: PriceElemsData,
        { shutterType, lamella, prices }
    ): PriceSegment[] {
        if (!shutterType?.id || !lamella?.id || shutterType?.onlyCover) {
            return [];
        }
        if (lamella.rotation === 180) {
            const price = prices.find(
                (el) =>
                    el.feature === 'ExternalVenetian.ShutterTypes' &&
                    +el.featureValue === +shutterType.id &&
                    el.feature2Value === 'externalVenetianLamellaRotation180'
            );
            if (price) {
                return [
                    {
                        type: 'externalVenetianLamellaRotation',
                        baseValue: (100 + Number(price.value)) / 100,
                        value: (100 + Number(price.value)) / 100,
                        valueType: 'percent',
                        data: {
                            shutterTypeId: shutterType.id,
                            shutterTypeExtId: shutterType.extId,
                            lamellaId: lamella.id,
                            lamellaExtId: lamella.extId,
                            lamellaRotation: lamella.rotation,
                        },
                    },
                ];
            }
        }
        return [];
    }

    @PriceFunc({
        shortName: 'externalVenetianAccessories',
        data: {
            shutterType: 'conf.shutterType',
            wallMount: 'conf.wallMount',
            topRailHolder: 'conf.topRailHolder',
            isolation: 'conf.isolation',
            brackets: 'conf.brackets',
            remoteControls: 'conf.remoteControls',
            prices: 'prices',
        },
    })
    accessories(
        { NoPriceCauses }: PriceElemsData,
        { shutterType, wallMount, topRailHolder, isolation, brackets, prices, remoteControls, }
    ): PriceSegment[] {
        const priceSegments: PriceSegment[] = [];
        if (wallMount?.id && wallMount?.amount && !shutterType?.onlyShutter) {
            const price = prices.find((el) => +el.accessoryId === +wallMount.id);
            if (price) {
                priceSegments.push({
                    type: 'externalVenetianWallMounts',
                    baseValue: price.value,
                    value: price.value,
                    quantity: wallMount.amount,
                    valueIsGross: price.isGross,
                    valueCurrency: price.currency,
                    taxRate: price.taxRate,
                    valueType: 'value',
                    data: {
                        id: wallMount.id,
                        name: wallMount.name,
                        count: wallMount.amount,
                        priceExtId: price.extId,
                        priceExtIdVariants: price.extIdVariants,
                        accessoryId: price.accessoryId,
                    },
                });
            }
        }
        if (topRailHolder?.id && topRailHolder?.amount && !shutterType?.onlyShutter) {
            const price = prices.find((el) => +el.accessoryId === +topRailHolder.id);
            if (price) {
                priceSegments.push({
                    type: 'externalVenetianTopRailHolders',
                    baseValue: price.value,
                    value: price.value,
                    quantity: topRailHolder.amount,
                    valueIsGross: price.isGross,
                    valueCurrency: price.currency,
                    taxRate: price.taxRate,
                    valueType: 'value',
                    data: {
                        id: topRailHolder.id,
                        name: topRailHolder.name,
                        count: topRailHolder.amount,
                        priceExtId: price.extId,
                        priceExtIdVariants: price.extIdVariants,
                        accessoryId: price.accessoryId,
                    },
                });
            }
        }

        if (brackets?.length > 0 && !shutterType?.onlyShutter) {
            brackets.forEach((bracket) => {
                if (bracket.checked) {
                    const price = prices.find((el) => +el.accessoryId === +bracket.id);
                    if (price) {
                        priceSegments.push({
                            type: 'externalVenetianBrackets',
                            baseValue: price.value,
                            value: price.value,
                            quantity: bracket.amount,
                            valueIsGross: price.isGross,
                            valueCurrency: price.currency,
                            taxRate: price.taxRate,
                            valueType: 'value',
                            data: {
                                id: bracket.id,
                                name: bracket.name,
                                count: bracket.amount,
                                priceExtId: price.extId,
                                priceExtIdVariants: price.extIdVariants,
                                accessoryId: price.accessoryId
                            },
                        });
                    }
                }
            });
        }

        if (remoteControls?.length > 0) {
            remoteControls.forEach((remoteControl) => {
                const price = prices.find((el) => +el.accessoryId === +remoteControl.id);
                if (price) {
                    priceSegments.push({
                        type: 'externalVenetianRemoteControls',
                        baseValue: price.value,
                        value: price.value,
                        quantity: remoteControl.amount,
                        valueIsGross: price.isGross,
                        valueCurrency: price.currency,
                        taxRate: price.taxRate,
                        valueType: 'value',
                        data: {
                            id: remoteControl.id,
                            name: remoteControl.name,
                            count: remoteControl.amount,
                            priceExtId: price.extId,
                            priceExtIdVariants: price.extIdVariants,
                            accessoryId: price.accessoryId,
                        },
                    });
                }
            });
        }
        return priceSegments;
    }

    @PriceFunc({
        shortName: 'externalVenetianGuides',
        data: {
            shutterType: 'conf.shutterType',
            shutters: 'conf.shutters',
            coverSegments: 'conf.coverSegments',
            cover: 'conf.cover',
            guides: 'conf.guides',
            guidesHandles: 'conf.guidesHandles',
            guidesCaps: 'conf.guidesCaps',
            guidesDrillingCaps: 'conf.guidesDrillingCaps',
            blindGuidanceType: 'conf.blindGuidanceType',
            prices: 'prices',
            layout: 'conf.layout',
        },
    })
    guides(
        { NoPriceCauses }: PriceElemsData,
        {
            shutterType,
            shutters,
            coverSegments,
            cover,
            guides,
            guidesHandles,
            guidesCaps,
            guidesDrillingCaps,
            blindGuidanceType,
            prices,
            layout,
        }
    ): PriceSegment[] {
        const priceSegments: PriceSegment[] = [];

        let hasInsetGuide = false;

        // eslint-disable-next-line max-statements
        shutters.forEach((shutter, index) => {
            const shutterGuides = guides.find((el) => +el.shutterId === +shutter.id);
            const shutterGuidesHandles = guidesHandles.find((el) => +el.shutterId === +shutter.id);
            const shutterGuidesCaps = guidesCaps.find((el) => +el.shutterId === +shutter.id);
            const coverSegment = coverSegments.find((el) => el.shuttersId.includes(+shutter.id));

            const connectionLeft = layout?.connections?.find((el) => el.next === shutter.id);
            const connectionRight = layout?.connections?.find((el) => el.previous === shutter.id);

            if (shutterGuidesHandles?.left && (!shutterGuidesHandles.left.default || shutterGuidesHandles.left.cornerBox || shutterGuidesHandles.left.alwaysPrice) && blindGuidanceType?.type !== 'guide_adaptation') {
                const price = prices.find(
                    (el) => +el.accessoryId === +shutterGuidesHandles.left.id
                );
                if (price) {
                    priceSegments.push({
                        type: 'externalVenetianGuideHandle',
                        baseValue: price.value,
                        value: price.value,
                        quantity: shutterGuidesHandles.left.amount,
                        valueIsGross: price.isGross,
                        valueCurrency: price.currency,
                        taxRate: price.taxRate,
                        valueType: 'value',
                        data: {
                            id: shutterGuidesHandles.left.id,
                            name: shutterGuidesHandles.left.name,
                            count: 1,
                            shutterId: shutter.id,
                            side: 'left',
                            priceExtId: price.extId,
                            priceExtIdVariants: price.extIdVariants,
                            accessoryId: price.accessoryId,
                        },
                    });
                }
            }
            if (shutterGuidesHandles?.right && (!shutterGuidesHandles.right.default || shutterGuidesHandles.right.cornerBox || shutterGuidesHandles.right.alwaysPrice) && blindGuidanceType?.type !== 'guide_adaptation') {
                const price = prices.find(
                    (el) => +el.accessoryId === +shutterGuidesHandles.right.id
                );
                if (price) {
                    priceSegments.push({
                        type: 'externalVenetianGuideHandle',
                        baseValue: price.value,
                        value: price.value,
                        quantity: shutterGuidesHandles.right.amount,
                        valueIsGross: price.isGross,
                        valueCurrency: price.currency,
                        taxRate: price.taxRate,
                        valueType: 'value',
                        data: {
                            id: shutterGuidesHandles.right.id,
                            name: shutterGuidesHandles.right.name,
                            count: 1,
                            shutterId: shutter.id,
                            side: 'right',
                            priceExtId: price.extId,
                            priceExtIdVariants: price.extIdVariants,
                            accessoryId: price.accessoryId,
                        },
                    });
                }
            }
            if (shutterGuides?.left && shutterGuides.left.length > 0 && blindGuidanceType?.type !== 'guide_adaptation') {
                hasInsetGuide = hasInsetGuide || shutterGuides.left.insetGuide;
                const price = prices.find((el) => +el.profileId === +shutterGuides.left.id && (!el.featureValue || +el.featureValue === shutterType.id));
                if (price && !shutterGuides.left.doubleGuide) {
                    priceSegments.push({
                        type: 'externalVenetianGuide',
                        baseValue: price.value,
                        value: price.value,
                        quantity: shutterGuides.left.length / 1000,
                        valueIsGross: price.isGross,
                        valueCurrency: price.currency,
                        taxRate: price.taxRate,
                        valueType: 'value',
                        data: {
                            id: shutterGuides.left.id,
                            name: shutterGuides.left.name,
                            type: shutterGuides.left.type,
                            count: 1,
                            shutterId: shutter.id,
                            side: 'left',
                            length: shutterGuides.left.length,
                            priceExtId: price.extId,
                            priceExtIdVariants: price.extIdVariants,
                            profileId: price.profileId,
                        },
                    });
                }

                if (shutterGuides?.left.child && shutterGuides.left.child.length > 0) {
                    const priceChild = prices.find(
                        (el) => +el.profileId === +shutterGuides.left.child.id && (!el.featureValue || +el.featureValue === shutterType.id)
                    );
                    if (priceChild) {
                        priceSegments.push({
                            type: 'externalVenetianGuide',
                            baseValue: priceChild.value,
                            value: priceChild.value,
                            quantity: shutterGuides.left.child.length / 1000,
                            valueIsGross: priceChild.isGross,
                            valueCurrency: priceChild.currency,
                            taxRate: priceChild.taxRate,
                            valueType: 'value',
                            data: {
                                id: shutterGuides.left.child.id,
                                name: shutterGuides.left.child.name,
                                type: shutterGuides.left.child.type,
                                count: 1,
                                shutterId: shutter.id,
                                side: 'left',
                                child: true,
                                length: shutterGuides.left.child.length,
                                priceExtId: price.extId,
                                priceExtIdVariants: price.extIdVariants,
                                profileId: price.profileId,
                            },
                        });
                    }
                }
            }
            if (cover?.type === 'blind_box' && !coverSegment.shuttersId.includes(shutters[index - 1]?.id) && (shutterGuides?.left?.type === 'cord' || blindGuidanceType?.type === 'cord_preparation') && !shutterType?.onlyShutter && (!connectionLeft || connectionLeft.coverSplit && connectionLeft.angle === '180deg')) {
                const priceMilling = prices.find(
                    (el) =>
                        el.feature === 'ExternalVenetian.BlindBoxesTypes' &&
                        +el.featureValue === +cover.blindBoxTypeId &&
                        el.feature2Value === 'externalVenetianBoxMilling'
                );
                if (priceMilling) {
                    priceSegments.push(
                        {
                            type: 'externalVenetianBoxMilling',
                            baseValue: priceMilling.value,
                            value: priceMilling.value,
                            valueType: 'value',
                            valueIsGross: priceMilling.isGross,
                            valueCurrency: priceMilling.currency,
                            taxRate: priceMilling.taxRate,
                            data: {
                                blindBoxTypeId: cover.blindBoxTypeId,
                                cordId: shutterGuides?.left?.id,
                                blindGuidanceType: blindGuidanceType?.type,
                                side: 'left',
                                count: 1,
                                priceExtId: priceMilling.extId,
                                priceExtIdVariants: priceMilling.extIdVariants,
                            },
                        },
                    );
                }
            }
            if (cover?.type === 'blind_box' && shutterGuides?.left?.type === 'blind_guidance' && (!connectionLeft || connectionLeft.coverSplit && connectionLeft.angle === '180deg') && !shutterType?.onlyShutter) {
                const priceMilling = prices.find(
                    (el) =>
                        el.feature === 'ExternalVenetian.BlindBoxesTypes' &&
                        +el.featureValue === +cover.blindBoxTypeId &&
                        el.feature2 === 'Profiles' &&
                        +el.feature2Value === +shutterGuides.left.id
                );
                if (priceMilling) {
                    priceSegments.push(
                        {
                            type: 'externalVenetianBoxGuideMilling',
                            baseValue: priceMilling.value,
                            value: priceMilling.value,
                            valueType: 'value',
                            valueIsGross: priceMilling.isGross,
                            valueCurrency: priceMilling.currency,
                            taxRate: priceMilling.taxRate,
                            data: {
                                blindBoxTypeId: cover.blindBoxTypeId,
                                guideId: shutterGuides?.left?.id,
                                blindGuidanceType: blindGuidanceType?.type,
                                side: 'left',
                                count: 1,
                                priceExtId: priceMilling.extId,
                                priceExtIdVariants: priceMilling.extIdVariants,
                            },
                        },
                    );
                }
            }
            if (shutterGuides?.right && shutterGuides.right.length > 0 && blindGuidanceType?.type !== 'guide_adaptation') {
                hasInsetGuide = hasInsetGuide || shutterGuides.right.insetGuide;
                const price = prices.find((el) => +el.profileId === +shutterGuides.right.id && (!el.featureValue || +el.featureValue === shutterType.id));
                if (price) {
                    priceSegments.push({
                        type: 'externalVenetianGuide',
                        baseValue: price.value,
                        value: price.value,
                        quantity: shutterGuides.right.length / 1000,
                        valueIsGross: price.isGross,
                        valueCurrency: price.currency,
                        taxRate: price.taxRate,
                        valueType: 'value',
                        data: {
                            id: shutterGuides.right.id,
                            name: shutterGuides.right.name,
                            type: shutterGuides.right.type,
                            count: 1,
                            shutterId: shutter.id,
                            side: 'right',
                            length: shutterGuides.right.length,
                            priceExtId: price.extId,
                            priceExtIdVariants: price.extIdVariants,
                            profileId: price.profileId,
                        },
                    });
                }

                if (shutterGuides?.right.child && shutterGuides.right.child.length > 0) {
                    const priceChild = prices.find(
                        (el) => +el.profileId === +shutterGuides.right.child.id && (!el.featureValue || +el.featureValue === shutterType.id)
                    );
                    if (priceChild) {
                        priceSegments.push({
                            type: 'externalVenetianGuide',
                            baseValue: priceChild.value,
                            value: priceChild.value,
                            quantity: shutterGuides.right.child.length / 1000,
                            valueIsGross: priceChild.isGross,
                            valueCurrency: priceChild.currency,
                            taxRate: priceChild.taxRate,
                            valueType: 'value',
                            data: {
                                id: shutterGuides.right.child.id,
                                name: shutterGuides.right.child.name,
                                type: shutterGuides.right.child.type,
                                count: 1,
                                shutterId: shutter.id,
                                side: 'right',
                                child: true,
                                length: shutterGuides.right.child.length,
                                priceExtId: price.extId,
                                priceExtIdVariants: price.extIdVariants,
                                profileId: price.profileId,
                            },
                        });
                    }
                }
            }

            if (cover?.type === 'blind_box' && !coverSegment.shuttersId.includes(shutters[index + 1]?.id) && (shutterGuides?.right?.type === 'cord' || blindGuidanceType?.type === 'cord_preparation') && !shutterType?.onlyShutter && (!connectionRight || connectionRight.coverSplit && connectionRight.angle === '180deg')) {
                const priceMilling = prices.find(
                    (el) =>
                        el.feature === 'ExternalVenetian.BlindBoxesTypes' &&
                        +el.featureValue === +cover.blindBoxTypeId &&
                        el.feature2Value === 'externalVenetianBoxMilling'
                );
                if (priceMilling) {
                    priceSegments.push(
                        {
                            type: 'externalVenetianBoxMilling',
                            baseValue: priceMilling.value,
                            value: priceMilling.value,
                            valueType: 'value',
                            valueIsGross: priceMilling.isGross,
                            valueCurrency: priceMilling.currency,
                            taxRate: priceMilling.taxRate,
                            data: {
                                blindBoxTypeId: cover.blindBoxTypeId,
                                cordId: shutterGuides?.right?.id,
                                blindGuidanceType: blindGuidanceType?.type,
                                side: 'right',
                                count: 1,
                                priceExtId: priceMilling.extId,
                                priceExtIdVariants: priceMilling.extIdVariants,
                            },
                        },
                    );
                }
            }

            if (cover?.type === 'blind_box' && shutterGuides?.right?.type === 'blind_guidance' && (!connectionRight || connectionRight.coverSplit && connectionRight.angle === '180deg') && !shutterType?.onlyShutter) {
                const priceMilling = prices.find(
                    (el) =>
                        el.feature === 'ExternalVenetian.BlindBoxesTypes' &&
                        +el.featureValue === +cover.blindBoxTypeId &&
                        el.feature2 === 'Profiles' &&
                        +el.feature2Value === +shutterGuides.right.id
                );
                if (priceMilling) {
                    priceSegments.push(
                        {
                            type: 'externalVenetianBoxGuideMilling',
                            baseValue: priceMilling.value,
                            value: priceMilling.value,
                            valueType: 'value',
                            valueIsGross: priceMilling.isGross,
                            valueCurrency: priceMilling.currency,
                            taxRate: priceMilling.taxRate,
                            data: {
                                blindBoxTypeId: cover.blindBoxTypeId,
                                guideId: shutterGuides?.right?.id,
                                blindGuidanceType: blindGuidanceType?.type,
                                side: 'right',
                                count: 1,
                                priceExtId: priceMilling.extId,
                                priceExtIdVariants: priceMilling.extIdVariants,
                            },
                        },
                    );
                }
            }

            if (shutterGuidesCaps?.left && (!shutterGuides.left.doubleGuide || !shutterGuidesCaps?.left?.singlePerGuide) && blindGuidanceType?.type !== 'guide_adaptation') {
                const price = prices.find((el) => +el.accessoryId === +shutterGuidesCaps.left.id && (!el.featureValue || +el.featureValue === shutterType.id));
                if (price) {
                    priceSegments.push({
                        type: 'externalVenetianGuideCap',
                        baseValue: price.value,
                        value: price.value,
                        valueIsGross: price.isGross,
                        taxRate: price.taxRate,
                        valueCurrency: price.currency,
                        valueType: 'value',
                        data: {
                            id: shutterGuidesCaps.left.id,
                            name: shutterGuidesCaps.left.name,
                            count: 1,
                            shutterId: shutter.id,
                            side: 'left',
                            priceExtId: price.extId,
                            priceExtIdVariants: price.extIdVariants,
                            accessoryId: price.accessoryId,
                        },
                    });
                }
            }

            if (shutterGuidesCaps?.right && blindGuidanceType?.type !== 'guide_adaptation') {
                const price = prices.find((el) => +el.accessoryId === +shutterGuidesCaps.right.id && (!el.featureValue || +el.featureValue === shutterType.id));
                if (price) {
                    priceSegments.push({
                        type: 'externalVenetianGuideCap',
                        baseValue: price.value,
                        value: price.value,
                        valueIsGross: price.isGross,
                        taxRate: price.taxRate,
                        valueCurrency: price.currency,
                        valueType: 'value',
                        data: {
                            id: shutterGuidesCaps.right.id,
                            name: shutterGuidesCaps.right.name,
                            count: 1,
                            shutterId: shutter.id,
                            side: 'right',
                            priceExtId: price.extId,
                            priceExtIdVariants: price.extIdVariants,
                            accessoryId: price.accessoryId,
                        },
                    });
                }
            }
        });

        if (shutterType?.id && hasInsetGuide && !cover?.hasIsolationDistance && blindGuidanceType?.type !== 'guide_adaptation') {
            const price = prices.find(
                (el) =>
                    el.feature === 'ExternalVenetian.ShutterTypes' &&
                    +el.featureValue === +shutterType.id &&
                    el.feature2Value === 'externalVenetianGuideInset'
            );
            if (price) {
                priceSegments.push({
                    type: 'externalVenetianGuideInset',
                    baseValue: (100 + Number(price.value)) / 100,
                    value: (100 + Number(price.value)) / 100,
                    valueType: 'percent',
                    data: {},
                });
            }
        }

        return priceSegments;
    }

    @PriceFunc({
        shortName: 'externalVenetianColors',
        data: {
            shutterType: 'conf.shutterType',
            cover: 'conf.cover',
            coverType: 'conf.coverType',
            colors: 'conf.colors',
            paintedEmbossedProfiles: 'conf.paintedEmbossedProfiles',
            boxPaintedSide: 'conf.boxPaintedSide',
            blindGuidanceType: 'conf.blindGuidanceType',
            prices: 'prices',
        },
    })
    colors(
        { NoPriceCauses }: PriceElemsData,
        { shutterType, cover, coverType, colors, paintedEmbossedProfiles, boxPaintedSide, blindGuidanceType, prices }
    ): PriceSegment[] {
        const priceSegments: PriceSegment[] = [];
        if (
            coverType &&
            paintedEmbossedProfiles &&
            coverType.paintedElements === 'bent_sheets_only' &&
            coverType.paintedEmbossedProfiles &&
            !shutterType?.onlyShutter
        ) {
            const price = prices.find(
                (el) =>
                    el.feature === 'ExternalVenetian.ShutterTypes' &&
                    +el.featureValue === +shutterType.id &&
                    el.feature2Value === 'externalVenetianBoxPaintedEmbossedProfiles'
            );
            if (price) {
                priceSegments.push({
                    type: 'externalVenetianBoxPaintedEmbossedProfiles',
                    baseValue: price.value !== null ? (100 + Number(price.value)) / 100 : null,
                    value: (100 + Number(price.value)) / 100,
                    valueType: 'percent',
                    data: {},
                });
            }
        }
        if (coverType && coverType.type !== 'blind_box' && boxPaintedSide === 'bilateral' && !shutterType?.onlyShutter) {
            const price = prices.find(
                (el) =>
                    el.feature === 'ExternalVenetian.ShutterTypes' &&
                    +el.featureValue === +shutterType.id &&
                    el.feature2Value === 'externalVenetianCoverSheetPaintedBilateral'
            );
            if (price) {
                priceSegments.push({
                    type: 'externalVenetianCoverSheetPaintedBilateral',
                    baseValue: (100 + Number(price.value)) / 100,
                    value: (100 + Number(price.value)) / 100,
                    valueType: 'percent',
                    data: {},
                });
            }
        }

        if (colors.cover?.id && colors.cover?.colorGroups && coverType && !shutterType?.onlyShutter) {
            const price = prices
                .filter(
                    (el) =>
                        el.feature === 'ConfigurationFeatures.ColorGroups' &&
                        (
                            colors.cover?.colorGroupsSurcharge ?
                            colors.cover?.colorGroupsSurcharge[shutterType.id + '-' + (coverType.blind_box === 'blind_box' ? 'blind_box' : 'blind_sheet')]?.includes(+el.featureValue) :
                            colors.cover?.colorGroups.map(Number).includes(+el.featureValue)
                        ) &&
                        el.feature2Value ===
                            (coverType.blind_box === 'blind_box'
                                ? 'externalVenetianBoxColor'
                                : 'externalVenetianCoverSheetColor')
                )
                .map((el) => el.value)
                .reduce((acu, cur) => {
                    if (cur == null) {
                        return null;
                    }
                    return (+cur > +acu ? cur : acu);
                }, 0);
            priceSegments.push({
                type: 'externalVenetianCoverColor',
                baseValue: price !== null ? (100 + price) / 100 : null,
                value: price !== null ? (100 + price) / 100 : null,
                valueType: 'percent',
                data: {
                    id: colors.cover.id,
                    name: colors.cover.name,
                    groups: (
                        colors.cover?.colorGroupsSurcharge ?
                        colors.cover?.colorGroupsSurcharge[shutterType.id + '-' + (coverType.blind_box === 'blind_box' ? 'blind_box' : 'blind_sheet')] :
                        colors.cover?.colorGroups.map(Number)
                    ),
                },
            });
        }
        if (colors.guidance?.id && colors.guidance?.colorGroups && blindGuidanceType?.type !== 'guide_adaptation') {
            const price = prices
                .filter(
                    (el) =>
                        el.feature === 'ConfigurationFeatures.ColorGroups' &&
                        (
                            colors.guidance?.colorGroupsSurcharge ?
                            colors.guidance?.colorGroupsSurcharge[shutterType.id + '-guidance']?.includes(+el.featureValue) :
                            colors.guidance?.colorGroups.map(Number).includes(+el.featureValue)
                        ) &&
                        el.feature2Value === 'externalVenetianGuideColor'
                )
                .map((el) => el.value)
                .reduce((acu, cur) => {
                    if (cur == null) {
                        return null;
                    }
                    return (+cur > +acu ? cur : acu);
                }, 0);
            priceSegments.push({
                type: 'externalVenetianGuideColor',
                baseValue: price !== null ? (100 + price) / 100 : null,
                value: price !== null ? (100 + price) / 100 : null,
                valueType: 'percent',
                data: {
                    id: colors.guidance.id,
                    name: colors.guidance.name,
                    groups: colors.guidance?.colorGroupsSurcharge ?
                        colors.guidance?.colorGroupsSurcharge[shutterType.id + '-guidance'] :
                        colors.guidance.colorGroups,
                },
            });
        }
        return priceSegments;
    }

    @PriceFunc({
        shortName: 'externalVenetianCover',
        data: {
            shutterType: 'conf.shutterType',
            coverHeight: 'conf.coverHeight',
            cover: 'conf.cover',
            coverType: 'conf.coverType',
            coverSegments: 'conf.coverSegments',
            straights: 'conf.straights',
            blindSheetExtension: 'conf.blindSheetExtension',
            isCoverWithSides: 'conf.isCoverWithSides',
            selectedSides: 'conf.selectedSides',
            prices: 'prices',
            pricesSettings: 'pricesSettings',
            shutters: 'conf.shutters',
            layout: 'conf.layout',
            guides: 'conf.guides',
        },
    })
    cover(
        { NoPriceCauses }: PriceElemsData,
        {
            shutterType,
            coverHeight,
            cover,
            coverType,
            coverSegments,
            straights,
            blindSheetExtension,
            isCoverWithSides,
            selectedSides,
            prices,
            pricesSettings,
            shutters,
            layout,
            guides,
        }
    ): PriceSegment[] {
        const priceSegments: PriceSegment[] = [];
        if (shutterType?.onlyShutter) {
            return priceSegments;
        }
        if (coverHeight && +coverHeight.height > +coverHeight.defaultHeight ||
            coverType && coverType.type === 'blind_box' && coverType.hasDeepenSurcharge
        ) {
            const price = prices.find(
                (el) =>
                    el.feature === 'ExternalVenetian.ShutterTypes' &&
                    +el.featureValue === +shutterType.id &&
                    el.feature2Value === 'externalVenetianBoxIncrease'
            );
            if (price) {
                priceSegments.push({
                    type: 'externalVenetianBoxIncrease',
                    baseValue: (100 + Number(price.value)) / 100,
                    value: (100 + Number(price.value)) / 100,
                    valueType: 'percent',
                    data: {
                        height: coverHeight.height,
                        defaultHeight: coverHeight.defaultHeight,
                        shutterTypeId: shutterType.id,
                        boxTypeId: cover.blindBoxTypeId,
                    },
                });
            }
        }
        if (coverSegments?.length && cover?.id) {
            const price = prices.find((el) => +el.profileId === cover.id);
            if (price) {
                coverSegments.forEach((segment) => {
                    priceSegments.push({
                        type: 'externalVenetianCoverSegments',
                        baseValue: price.value,
                        value: price.value,
                        quantity: segment.length / 1000,
                        valueIsGross: price.isGross,
                        valueCurrency: price.currency,
                        taxRate: price.taxRate,
                        valueType: 'value',
                        data: {
                            id: cover.id,
                            name: cover.name,
                            length: segment.length,
                            segmentId: segment.id,
                            type: cover.type,
                            priceExtId: price.extId,
                            priceExtIdVariants: price.extIdVariants,
                            profileId: price.profileId,
                        },
                    });
                });
            }
            if (cover?.type === 'blind_sheet' && cover?.blindSheetLinkId && coverSegments?.length > 1) {
                const blindSheetLinkPrice = prices.find((el) => +el.accessoryId === +cover?.blindSheetLinkId);
                if (blindSheetLinkPrice) {
                    priceSegments.push({
                        type: 'externalVenetianBlindSheetLinks',
                        baseValue: blindSheetLinkPrice.value,
                        value: blindSheetLinkPrice.value,
                        quantity: coverSegments?.length - 1,
                        valueIsGross: blindSheetLinkPrice.isGross,
                        valueCurrency: blindSheetLinkPrice.currency,
                        taxRate: blindSheetLinkPrice.taxRate,
                        valueType: 'value',
                        data: {
                            id: cover?.blindSheetLinkId,
                            name: cover?.blindSheetLinkName,
                            priceExtId: blindSheetLinkPrice.extId,
                            priceExtIdVariants: blindSheetLinkPrice.extIdVariants,
                            accessoryId: blindSheetLinkPrice.accessoryId,
                        },
                    });
                }
            }
            if (cover?.type === 'blind_sheet_sets' && cover.associatedProfiles?.length > 0) {
                cover.associatedProfiles.forEach((assoc) => {
                    const priceSheet = prices.find((el) => +el.profileId === assoc.id);
                    if (priceSheet) {
                        coverSegments.forEach((segment) => {
                            priceSegments.push({
                                type: 'externalVenetianCoverSegments',
                                baseValue: priceSheet.value,
                                value: priceSheet.value,
                                quantity: segment.length / 1000,
                                valueIsGross: priceSheet.isGross,
                                valueCurrency: priceSheet.currency,
                                taxRate: priceSheet.taxRate,
                                valueType: 'value',
                                data: {
                                    id: assoc.id,
                                    name: assoc.name,
                                    length: segment.length,
                                    segmentId: segment.id,
                                    type: assoc.type,
                                    priceExtId: priceSheet.extId,
                                    priceExtIdVariants: priceSheet.extIdVariants,
                                    profileId: priceSheet.profileId,
                                },
                            });
                        });
                        if (assoc?.blindSheetLinkId && coverSegments?.length > 1) {
                            const blindSheetLinkPrice = prices.find((el) => +el.accessoryId === +assoc?.blindSheetLinkId);
                            if (blindSheetLinkPrice) {
                                priceSegments.push({
                                    type: 'externalVenetianBlindSheetLinks',
                                    baseValue: blindSheetLinkPrice.value,
                                    value: blindSheetLinkPrice.value,
                                    quantity: coverSegments?.length - 1,
                                    valueIsGross: blindSheetLinkPrice.isGross,
                                    valueCurrency: blindSheetLinkPrice.currency,
                                    taxRate: blindSheetLinkPrice.taxRate,
                                    valueType: 'value',
                                    data: {
                                        id: assoc?.blindSheetLinkId,
                                        name: assoc?.blindSheetLinkName,
                                        priceExtId: blindSheetLinkPrice.extId,
                                        priceExtIdVariants: blindSheetLinkPrice.extIdVariants,
                                        accessoryId: blindSheetLinkPrice.accessoryId,
                                    },
                                });
                            }
                        }
                    }
                });
            }
        }
        if (blindSheetExtension?.id && blindSheetExtension?.amount) {
            const price = prices.find((el) => +el.accessoryId === +blindSheetExtension.id);
            if (price) {
                priceSegments.push({
                    type: 'externalVenetianBlindSheetExtensions',
                    baseValue: price.value,
                    value: price.value,
                    quantity: blindSheetExtension.amount,
                    valueIsGross: price.isGross,
                    valueCurrency: price.currency,
                    taxRate: price.taxRate,
                    valueType: 'value',
                    data: {
                        id: blindSheetExtension.id,
                        name: blindSheetExtension.name,
                        count: blindSheetExtension.amount,
                        priceExtId: price.extId,
                        priceExtIdVariants: price.extIdVariants,
                        accessoryId: price.accessoryId,
                    },
                });
            }
        }

        if (selectedSides?.id && isCoverWithSides) {
            const price = prices.find((el) => +el.accessoryId === +selectedSides.id);
            if (price) {
                priceSegments.push({
                    type: 'externalVenetianBlindBoxSide',
                    baseValue: price.value,
                    value: price.value,
                    quantity: 2,
                    valueIsGross: price.isGross,
                    valueCurrency: price.currency,
                    taxRate: price.taxRate,
                    valueType: 'value',
                    data: {
                        id: selectedSides.id,
                        name: selectedSides.name,
                        count: 2,
                        priceExtId: price.extId,
                        priceExtIdVariants: price.extIdVariants,
                        accessoryId: price.accessoryId,
                    },
                });
            }
        }

        shutters.forEach((shutter, index) => {
            const coverSegment = coverSegments.find((el) => el.shuttersId.includes(+shutter.id));
            const connectionLeft = layout?.connections?.find((el) => el.next === shutter.id);
            const connectionRight = layout?.connections?.find((el) => el.previous === shutter.id);
            const guide = guides.find((el) => +el.shutterId === +shutter.id);

            if (connectionLeft && connectionLeft.angle !== '180deg') {
                const price = prices.find(
                    (el) =>
                        el.feature === 'ExternalVenetian.BlindBoxesTypes' &&
                        +el.featureValue === +cover.blindBoxTypeId &&
                        el.feature2Value === 'externalVenetianCornerBox'
                );
                if (price) {
                    priceSegments.push({
                        type: 'externalVenetianCornerBox',
                        baseValue: price.value,
                        value: price.value,
                        valueType: 'value',
                        valueIsGross: price.isGross,
                        valueCurrency: price.currency,
                        taxRate: price.taxRate,
                        data: {
                            blindBoxTypeId: cover.blindBoxTypeId,
                            angle: connectionLeft.angle,
                            count: 1,
                            side: 'left',
                            priceExtId: price.extId,
                            priceExtIdVariants: price.extIdVariants,
                        },
                    });
                }
            }

            if (connectionRight && connectionRight.angle !== '180deg') {
                const price = prices.find(
                    (el) =>
                        el.feature === 'ExternalVenetian.BlindBoxesTypes' &&
                        +el.featureValue === +cover.blindBoxTypeId &&
                        el.feature2Value === 'externalVenetianCornerBox'
                );
                if (price) {
                    priceSegments.push({
                        type: 'externalVenetianCornerBox',
                        baseValue: price.value,
                        value: price.value,
                        valueType: 'value',
                        valueIsGross: price.isGross,
                        valueCurrency: price.currency,
                        taxRate: price.taxRate,
                        data: {
                            blindBoxTypeId: cover.blindBoxTypeId,
                            angle: connectionRight.angle,
                            count: 1,
                            side: 'right',
                            priceExtId: price.extId,
                            priceExtIdVariants: price.extIdVariants,
                        },
                    });
                }
            }
        });

        return priceSegments;
    }

    @PriceFunc({
        shortName: 'externalVenetianDrives',
        data: {
            shutters: 'conf.shutters',
            drives: 'conf.drives',
            driveShaftLink: 'conf.driveShaftLink',
            prices: 'prices',
            pricesSettings: 'pricesSettings',
        },
    })
    drives(
        { NoPriceCauses }: PriceElemsData,
        { shutters, drives, driveShaftLink, prices, pricesSettings }
    ): PriceSegment[] {
        const priceSegments: PriceSegment[] = [];

        if (drives?.length > 0) {
            let linksCount = 0;

            for (const drive of drives) {
                if (drive.linkedNext !== null) {
                    linksCount++;
                }
                if (drive.linkedPrev != null) {
                    continue;
                }
                if (drive.crank?.crankRod && drive.crank?.changedLength && drive.crank?.length > 0) {
                    const shutter = shutters.find((el) => +el.id === +drive.shutterId);
                    const standardLength = shutter?.height && drive.crank.crankRod.standardLength &&
                        Math.round((shutter.height * drive.crank.crankRod.standardLength) / 100)
                    const price = prices.find((el) => +el.profileId === +drive.crank?.crankRod.id);
                    if (price && (drive.crank?.length > standardLength)) {
                        priceSegments.push({
                            type: 'externalVenetianCrankRod',
                            baseValue: price.value,
                            value: price.value,
                            quantity: (drive.crank?.length - standardLength) / 1000,
                            valueIsGross: price.isGross,
                            valueCurrency: price.currency,
                            taxRate: price.taxRate,
                            valueType: 'value',
                            data: {
                                id: drive.crank?.crankRod.id,
                                name: drive.crank?.crankRod.name,
                                count: drive.amount,
                                length: drive.crank?.length,
                                shutterId: drive.shutterId,
                                priceExtId: price.extId,
                                priceExtIdVariants: price.extIdVariants,
                                profileId: price.profileId,
                            },
                        });
                    }
                }
                if (drive.driveId) {
                    const price = prices.find(
                        (el) =>
                            el.feature === 'ConfigurationFeatures.RollerShutterDrives' &&
                            +el.featureValue === +drive.driveId &&
                            +el.feature2Value === (+drive.driveOptionId || 1)
                    );

                    if (price) {
                        priceSegments.push({
                            type: 'externalVenetianDrive',
                            baseValue: price.value,
                            value: price.value,
                            valueCurrency: price.currency,
                            valueIsGross: price.isGross,
                            taxRate: price.taxRate,
                            valueType: 'value',
                            data: {
                                id: drive.driveId,
                                name: drive.name,
                                optionId: drive.driveOptionId || 1,
                                shutterId: drive.shutterId,
                                priceExtId: price.extId,
                                priceExtIdVariants: price.extIdVariants,
                            },
                        });
                    }
                }
                if (drive.crank?.crankGrommet?.id) {
                    const price = prices.find((el) => +el.accessoryId === +drive.crank?.crankGrommet.id);
                    if (price) {
                        priceSegments.push({
                            type: 'externalVenetianCrankGrommet',
                            baseValue: price.value,
                            value: price.value,
                            valueIsGross: price.isGross,
                            valueCurrency: price.currency,
                            taxRate: price.taxRate,
                            valueType: 'value',
                            data: {
                                id: drive.crank?.crankGrommet.id,
                                name: drive.crank?.crankGrommet.name,
                                count: drive.amount,
                                priceExtId: price.extId,
                                priceExtIdVariants: price.extIdVariants,
                                accessoryId: price.accessoryId,
                            },
                        });
                    }
                }
            }

            if (driveShaftLink?.id && linksCount > 0) {
                const price = prices.find((el) => +el.accessoryId === +driveShaftLink.id);
                if (price) {
                    priceSegments.push({
                        type: 'externalVenetianDriveShaftLink',
                        baseValue: price.value,
                        value: price.value,
                        quantity: linksCount,
                        valueCurrency: price.currency,
                        valueIsGross: price.isGross,
                        taxRate: price.taxRate,
                        valueType: 'value',
                        data: {
                            id: driveShaftLink.id,
                            name: driveShaftLink.name,
                            count: linksCount,
                            priceExtId: price.extId,
                            priceExtIdVariants: price.extIdVariants,
                            accessoryId: price.accessoryId,
                        },
                    });
                }
            }
        }

        return priceSegments;
    }

    @PriceFunc({
        shortName: 'externalVenetianMosquitoNet',
        data: {
            mosquitoScreenType: 'conf.mosquitoScreenType',
            mosquitoScreens: 'conf.mosquitoScreens',
            prices: 'prices',
        },
    })
    mosquitoNet(
        { NoPriceCauses }: PriceElemsData,
        { mosquitoScreenType, mosquitoScreens, prices }
    ): PriceSegment[] {
        const priceSegments: PriceSegment[] = [];
        if (mosquitoScreenType?.id && mosquitoScreens?.length) {
            const price = prices.find((el) => +el.accessoryId === +mosquitoScreenType.id);
            const amount = mosquitoScreens.length;
            if (price) {
                priceSegments.push({
                    type: 'externalVenetianMosquitoScreenType',
                    baseValue: price.value,
                    value: price.value,
                    quantity: amount,
                    valueIsGross: price.isGross,
                    valueCurrency: price.currency,
                    taxRate: price.taxRate,
                    valueType: 'value',
                    data: {
                        id: mosquitoScreenType.id,
                        name: mosquitoScreenType.name,
                        amount,
                        priceExtId: price.extId,
                        priceExtIdVariants: price.extIdVariants,
                        accessoryId: mosquitoScreenType.id,
                    },
                });
            }
        }
        return priceSegments;
    }
}
